
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import { ExactSyncService, IExactExportResult, IExactUnsyncedDocument, IExactUnsyncedOverview } from "@/services/exact-sync-service";
import { Job } from "@/models/job";
import { JobService } from "@/services/job-service";
import { MainSocketService } from "@/services/main-socket-service";

function toIsoDate(d:Date):string {
	let m = String(d.getMonth() + 1).padStart(2, "0");
	let day = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${m}-${day}`;
}

function twoMonthsAgo():Date {
	let d = new Date();
	d.setMonth(d.getMonth() - 2);
	d.setDate(1);
	return d;
}

export default Vue.extend({
	data() {
		return {
			tab: 0,
			from: twoMonthsAgo(),
			upTo: new Date(),
			loading: true,
			sending: false,
			overview: null as IExactUnsyncedOverview | null,
			selectedKeys: [] as string[],
			expandedKey: null as string | null,
			job: null as Job | null,
			jobSubs: [] as string[]
		};
	},
	props: {
		dossierName: {
			type: String,
			default: ""
		}
	},
	computed: {
		tabItems():ISelectItem<number>[] {
			let s = this.overview ? this.overview.Sales.length : 0;
			let p = this.overview ? this.overview.Purchases.length : 0;
			return [
				{value: 0, text: this.$ct("tab-sales", {count: s}).toString(), icon: "file-invoice"},
				{value: 1, text: this.$ct("tab-purchases", {count: p}).toString(), icon: "shopping-cart"}
			];
		},
		isPurchases():boolean {
			return this.tab == 1;
		},
		documents():IExactUnsyncedDocument[] {
			if (!this.overview) return [];
			return this.isPurchases ? this.overview.Purchases : this.overview.Sales;
		},
		truncated():boolean {
			if (!this.overview) return false;
			return this.isPurchases ? this.overview.PurchasesTruncated : this.overview.SalesTruncated;
		},
		totalAmount():number {
			return this.documents.reduce((sum, d) => sum + (d.IsCreditNote ? -d.Total : d.Total), 0);
		},
		failedCount():number {
			return this.documents.filter(d => d.SyncStatus == "error").length;
		},
		selectedDocuments():IExactUnsyncedDocument[] {
			return this.documents.filter(d => this.selectedKeys.includes(this.keyOf(d)));
		},
		selectedCount():number {
			return this.selectedDocuments.length;
		},
		allSelected():boolean {
			return this.documents.length > 0 && this.selectedCount == this.documents.length;
		}
	},
	watch: {
		tab() {
			this.selectedKeys = [];
			this.expandedKey = null;
		}
	},
	methods: {
		close() {
			this.$emit("close");
		},
		keyOf(d:IExactUnsyncedDocument):string {
			return `${d.ID}-${d.BookYear}`;
		},
		isSelected(d:IExactUnsyncedDocument):boolean {
			return this.selectedKeys.includes(this.keyOf(d));
		},
		toggle(d:IExactUnsyncedDocument, checked:boolean) {
			let k = this.keyOf(d);
			if (checked && !this.selectedKeys.includes(k)) {
				this.selectedKeys.push(k);
			} else if (!checked) {
				this.selectedKeys = this.selectedKeys.filter(x => x != k);
			}
		},
		toggleAll(checked:boolean) {
			this.selectedKeys = checked ? this.documents.map(d => this.keyOf(d)) : [];
		},
		toggleError(d:IExactUnsyncedDocument) {
			let k = this.keyOf(d);
			this.expandedKey = this.expandedKey === k ? null : k;
		},
		formatDate(d:string):string {
			if (!d) return "";
			return new Date(d).toLocaleDateString();
		},
		formatMoney(n:number):string {
			return n.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
		},
		statusLabel(d:IExactUnsyncedDocument):string {
			if (d.SyncStatus == "error") return this.$ct("status.error", {attempts: d.Attempts}).toString();
			if (d.SyncStatus == "pending") return this.$ct("status.pending").toString();
			return this.$ct("status.never").toString();
		},
		statusClass(d:IExactUnsyncedDocument):string {
			if (d.SyncStatus == "error") return "pill-error";
			if (d.SyncStatus == "pending") return "pill-pending";
			return "pill-never";
		},
		async load() {
			this.loading = true;
			this.selectedKeys = [];
			this.expandedKey = null;
			try {
				this.overview = await ExactSyncService.getUnsynced(toIsoDate(new Date(this.from)));
			} catch (err) {
				this.$wf.notify(this.$ct("load-error").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.loading = false;
			}
		},
		async markSelected() {
			let docs = this.selectedDocuments;
			if (!docs.length) return;
			let ok = await this.$wf.confirm(
				this.$ct("confirm-mark-selected", {count: docs.length}).toString(),
				this.$ct("confirm-yes").toString(),
				this.$ct("common.cancel").toString(),
				this.$ct("mark-selected", {count: docs.length}).toString(),
				"warning"
			);
			if (ok != 1) return;
			try {
				await ExactSyncService.markExternal(this.isPurchases
					? {purchases: docs.map(d => ({ID: d.ID, BookYear: d.BookYear}))}
					: {saleIds: docs.map(d => d.ID)}
				);
				this.$wf.notify(this.$ct("marked", {count: docs.length}).toString(), "success", 4000, "check");
				await this.load();
			} catch (err) {
				this.$wf.notify(this.$ct("mark-error").toString(), "error", 5000, "exclamation-triangle");
			}
		},
		async markUpTo() {
			let picked = toIsoDate(new Date(this.upTo));
			let ok = await this.$wf.confirm(
				this.$ct("confirm-mark-up-to", {date: new Date(this.upTo).toLocaleDateString()}).toString(),
				this.$ct("confirm-yes").toString(),
				this.$ct("common.cancel").toString(),
				this.$ct("mark-up-to").toString(),
				"warning"
			);
			if (ok != 1) return;
			try {
				let res = await ExactSyncService.markExternal({
					upTo: picked,
					includeSales: !this.isPurchases,
					includePurchases: this.isPurchases
				});
				let count = this.isPurchases ? res.Purchases : res.Sales;
				this.$wf.notify(this.$ct("marked", {count}).toString(), "success", 4000, "check");
				await this.load();
			} catch (err) {
				this.$wf.notify(this.$ct("mark-error").toString(), "error", 5000, "exclamation-triangle");
			}
		},
		async sendSelected() {
			let docs = this.selectedDocuments;
			if (!docs.length) return;
			try {
				this.sending = true;
				this.job = null;
				let j = this.isPurchases
					? await ExactSyncService.exportPurchasePeriod({purchases: docs.map(d => ({ID: d.ID, BookYear: d.BookYear}))})
					: await ExactSyncService.exportPeriod({saleIds: docs.map(d => d.ID)});
				this.subscribeToJob(j);
			} catch (err) {
				this.sending = false;
				this.$wf.notify(this.$ct("send-error").toString(), "error", 5000, "exclamation-triangle");
			}
		},
		subscribeToJob(j:Job) {
			JobService.on(`job-started/${j.getID()}`, this.onJobStarted);
			JobService.on(`job-stopped/${j.getID()}`, this.onJobStopped);
			this.jobSubs.push(`job-started/${j.getID()}`);
			this.jobSubs.push(`job-stopped/${j.getID()}`);
			MainSocketService.on(`/plugins/exactonline/export-done/${j.getID()}`, this.onExportDone, this);
		},
		onJobStarted(job:Job) {
			this.job = job;
		},
		onJobStopped(job:Job) {
			if (!this.sending) return;
			if (job.Error) {
				this.sending = false;
				this.$wf.notify(this.$ct("send-error").toString(), "error", 5000, "exclamation-triangle");
			}
		},
		async onExportDone(result:IExactExportResult) {
			this.sending = false;
			this.$wf.notify(
				this.$ct("send-result", {created: result.Created, failed: result.Failed}).toString(),
				result.Failed > 0 ? "warning" : "success",
				6000,
				result.Failed > 0 ? "exclamation-triangle" : "check"
			);
			await this.load();
		}
	},
	mounted() {
		this.load();
	},
	destroyed() {
		for (let sub of this.jobSubs) {
			JobService.off(sub, sub.startsWith("job-started") ? this.onJobStarted : this.onJobStopped);
		}
		MainSocketService.offAll(this);
	},
	i18nEx: {
		componentPrefix: "components.accountant-ExactUnsyncedModal"
	}
});
